/**
 * useGeolocation Hook
 * 
 * Detects user's location via IP-based geolocation API.
 * Uses ipwho.is as primary (no rate limit), ipapi.co as fallback.
 * Persists location to localStorage to avoid repeated API calls.
 * Falls back to US as default if all APIs fail.
 * 
 * LOCALE OVERRIDE: When the user is on a locale-prefixed route (e.g., /uk-en),
 * the geolocation MERGES locale data with real IP data:
 * - countryCode is forced to match the locale (for pricing/shipping cost)
 * - city and region are taken from real IP data when available
 * - Falls back to locale defaults only when IP data is unavailable
 * 
 * This ensures "Ships to Glasgow" (real city) with correct UK pricing,
 * rather than hardcoded "Ships to London".
 */

import { useState, useEffect, useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import { useLocale } from '@/contexts/LocaleContext';

export interface GeoLocation {
  country: string;
  countryCode: string;
  region: string;       // State/region code (e.g., "CA", "NSW", "SCT")
  regionName: string;   // Full state/region name (e.g., "California", "New South Wales", "Scotland")
  city: string;
  isLoading: boolean;
  error: string | null;
}

const STORAGE_KEY = 'fluffco_geolocation';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

interface StoredLocation {
  data: Omit<GeoLocation, 'isLoading' | 'error'>;
  timestamp: number;
}

/**
 * Locale-to-country FALLBACK mapping.
 * Only used when real IP data is unavailable for the locale's country.
 * 
 * For EU: country name defaults to "Europe" but will be replaced by real
 * IP country (Germany, France, etc.) when available.
 */
const LOCALE_FALLBACK_MAP: Record<string, { country: string; countryCode: string; city: string; region: string; regionName: string }> = {
  'GB': { country: 'United Kingdom', countryCode: 'GB', city: 'London', region: '', regionName: 'England' },
  'AU': { country: 'Australia', countryCode: 'AU', city: 'Sydney', region: 'NSW', regionName: 'New South Wales' },
  'EU': { country: 'Europe', countryCode: 'EU', city: '', region: '', regionName: '' },
  'CA': { country: 'Canada', countryCode: 'CA', city: 'Toronto', region: 'ON', regionName: 'Ontario' },
  'NZ': { country: 'New Zealand', countryCode: 'NZ', city: 'Auckland', region: '', regionName: 'Auckland' },
};

/**
 * EU member state country codes.
 * Used to determine if an IP-detected country belongs to the EU locale.
 */
const EU_COUNTRY_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // Also include nearby non-EU European countries that use EUR or are commonly grouped
  'NO', 'CH', 'IS',
]);

/**
 * Check if a country code belongs to the EU/European locale group
 */
export function isEuCountry(countryCode: string): boolean {
  return EU_COUNTRY_CODES.has(countryCode);
}

// Get cached location from localStorage
function getCachedLocation(): GeoLocation | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    
    const parsed: StoredLocation = JSON.parse(stored);
    const now = Date.now();
    
    // Check if cache is still valid (24 hours)
    if (now - parsed.timestamp < CACHE_DURATION) {
      return {
        ...parsed.data,
        isLoading: false,
        error: null,
      };
    }
    
    // Cache expired, remove it
    localStorage.removeItem(STORAGE_KEY);
    return null;
  } catch {
    return null;
  }
}

// Save location to localStorage
function setCachedLocation(location: GeoLocation): void {
  try {
    const toStore: StoredLocation = {
      data: {
        country: location.country,
        countryCode: location.countryCode,
        region: location.region,
        regionName: location.regionName,
        city: location.city,
      },
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
  } catch {
    // localStorage might be full or disabled, ignore
  }
}

// In-memory cache for current session (stores the REAL IP-based location)
let memoryCache: GeoLocation | null = null;

export function useGeolocation(): GeoLocation {
  const { locale, isInternational } = useLocale();
  
  const [ipLocation, setIpLocation] = useState<GeoLocation>(() => {
    // First check memory cache
    if (memoryCache) {
      return memoryCache;
    }
    
    // Then check localStorage
    const cached = getCachedLocation();
    if (cached) {
      memoryCache = cached;
      return cached;
    }
    
    return {
      country: '',
      countryCode: '',
      region: '',
      regionName: '',
      city: '',
      isLoading: true,
      error: null,
    };
  });

  useEffect(() => {
    // Skip if already have valid location in memory
    if (memoryCache && memoryCache.country) {
      return;
    }
    
    // Check localStorage again (in case it was set by another component)
    const cached = getCachedLocation();
    if (cached && cached.country) {
      memoryCache = cached;
      setIpLocation(cached);
      return;
    }

    const fetchLocation = async () => {
      // Try primary API first, then fallback
      const apis = [
        {
          url: 'https://ipwho.is/',
          parse: (data: any) => ({
            country: data.country || '',
            countryCode: data.country_code || '',
            region: data.region_code || '',
            regionName: data.region || '',
            city: data.city || '',
          }),
          validate: (data: any) => data.success !== false && data.country_code,
        },
        {
          url: 'https://ipapi.co/json/',
          parse: (data: any) => ({
            country: data.country_name || '',
            countryCode: data.country_code || '',
            region: data.region_code || '',
            regionName: data.region || '',
            city: data.city || '',
          }),
          validate: (data: any) => !data.error && (data.country_name || data.country_code),
        },
      ];

      for (const api of apis) {
        try {
          const response = await fetch(api.url, {
            method: 'GET',
            headers: { 'Accept': 'application/json' },
          });

          if (!response.ok) continue;

          const data = await response.json();
          if (!api.validate(data)) continue;

          const parsed = api.parse(data);
          const newLocation: GeoLocation = {
            ...parsed,
            isLoading: false,
            error: null,
          };

          // Cache in memory and localStorage
          memoryCache = newLocation;
          setCachedLocation(newLocation);
          setIpLocation(newLocation);
          return; // Success, exit
        } catch {
          continue; // Try next API
        }
      }

      // All APIs failed - use US as default (most visitors)
      console.warn('[Geolocation] All APIs failed, defaulting to US');
      const defaultLocation: GeoLocation = {
        country: 'United States',
        countryCode: 'US',
        region: '',
        regionName: '',
        city: '',
        isLoading: false,
        error: null,
      };
      memoryCache = defaultLocation;
      setCachedLocation(defaultLocation);
      setIpLocation(defaultLocation);
    };

    fetchLocation();
  }, []);

  // LOCALE OVERRIDE: When on a locale-prefixed route (e.g., /uk-en),
  // MERGE locale data with real IP data instead of replacing entirely.
  // - countryCode is forced to match the locale (for pricing/shipping calculations)
  // - city, region, country NAME are taken from real IP when available
  // - Falls back to locale defaults only when IP data is unavailable
  const effectiveLocation = useMemo<GeoLocation>(() => {
    if (isInternational && locale.countryCode && locale.countryCode !== 'US') {
      const fallback = LOCALE_FALLBACK_MAP[locale.countryCode];
      const ipHasData = ipLocation.country && !ipLocation.isLoading;
      
      // Check if the IP-detected country matches the locale's expected country/region
      const ipMatchesLocale = ipHasData && (
        // Direct country match (e.g., IP=GB, locale=GB)
        ipLocation.countryCode === locale.countryCode ||
        // EU locale: IP country is an EU member state
        (locale.countryCode === 'EU' && isEuCountry(ipLocation.countryCode))
      );

      if (ipMatchesLocale) {
        // IP matches locale — use real IP data for city/region/country name
        // but force countryCode to locale's code (important for EU: keeps 'EU' for pricing)
        return {
          country: ipLocation.country,           // Real country name (e.g., "Germany", "Scotland")
          countryCode: locale.countryCode,        // Forced to locale code (e.g., 'GB', 'EU')
          region: ipLocation.region,              // Real region code (e.g., "SCT", "BY")
          regionName: ipLocation.regionName,      // Real region name (e.g., "Scotland", "Bavaria")
          city: ipLocation.city,                  // Real city (e.g., "Glasgow", "Munich")
          isLoading: false,
          error: null,
        };
      }

      // IP doesn't match locale (e.g., US visitor on /uk-en, or IP not loaded yet)
      // Use locale fallback defaults
      if (fallback) {
        return {
          country: fallback.country,
          countryCode: fallback.countryCode,
          region: fallback.region,
          regionName: fallback.regionName,
          city: fallback.city,
          isLoading: ipLocation.isLoading, // Pass through loading state
          error: null,
        };
      }

      // Unknown locale — use locale name as country
      return {
        country: locale.name,
        countryCode: locale.countryCode,
        region: '',
        regionName: '',
        city: '',
        isLoading: ipLocation.isLoading,
        error: null,
      };
    }
    return ipLocation;
  }, [isInternational, locale.countryCode, locale.name, ipLocation]);

  return effectiveLocation;
}

// Default shipping times (used as fallback if settings not loaded)
const DEFAULT_SHIPPING_TIMES: Record<string, string> = {
  US: '2-3 days',
  INTERNATIONAL: '5-8 days',
};

// Shipping costs for international orders
export const INTERNATIONAL_SHIPPING_COSTS: Record<number, number> = {
  1: 10,  // $10 for 1 pillow
  2: 15,  // $15 for 2 pillows
  3: 20,  // $20 for 3+ pillows
};

/**
 * Get shipping cost based on quantity (pillows + upsells)
 * US: Free shipping
 * International: $10 (1), $15 (2), $20 (3+)
 */
export function getShippingCost(countryCode: string, quantity: number): number {
  // US gets free shipping
  if (countryCode === 'US') {
    return 0;
  }
  
  // International shipping costs
  if (quantity <= 1) return 10;
  if (quantity === 2) return 15;
  return 20; // 3 or more
}

/**
 * Check if user is in the US (for free shipping badge visibility)
 */
export function isUSVisitor(countryCode: string): boolean {
  return countryCode === 'US';
}

/**
 * Get estimated delivery days based on country
 * US: 2-3 days, International: 5-8 days
 * @param includePrefix - If true, includes "Delivers in" prefix
 */
export function getDeliveryEstimate(countryCode: string, settings?: Record<string, string>, includePrefix: boolean = true): string {
  // US domestic shipping
  let days: string;
  if (countryCode === 'US') {
    days = settings?.shipping_time_us || DEFAULT_SHIPPING_TIMES.US;
  } else {
    // All other countries (international)
    days = settings?.shipping_time_international || DEFAULT_SHIPPING_TIMES.INTERNATIONAL;
  }
  
  return includePrefix ? `Delivers in ${days}` : days;
}

/**
 * Check if we ship to a country
 * We ship worldwide!
 */
export function isShippingAvailable(countryCode: string): boolean {
  // We ship to all countries worldwide
  return true;
}

/**
 * Hook to get shipping settings from database
 */
export function useShippingSettings() {
  const { data: settings } = trpc.settings.getAll.useQuery({ category: 'shipping' });
  
  // Convert array to key-value map
  const settingsMap: Record<string, string> = {};
  settings?.forEach(s => {
    settingsMap[s.key] = s.value;
  });
  
  return settingsMap;
}

/**
 * Hook to get the non-US badge text from settings
 * Returns the configured badge text or 'Free Returns' as default
 */
export function useNonUsBadgeText(): string {
  const settings = useShippingSettings();
  return settings?.non_us_badge_text || 'Free Returns';
}

/**
 * Hook to get the non-US alternative USP (replaces "Made in USA" for international visitors)
 * Returns the configured alternative USP or 'Award-Winning Quality' as default
 */
export function useNonUsAlternativeUsp(): { title: string; description: string } {
  const settings = useShippingSettings();
  return {
    title: settings?.non_us_alternative_usp_title || 'Award-Winning Quality',
    description: settings?.non_us_alternative_usp_description || 'Dermatologically tested & clinically proven'
  };
}

/**
 * Helper to format location text for display.
 * US: "City, State" (e.g., "Austin, TX")
 * International: "City, Country" or just "Country" if no city
 * 
 * This is the SINGLE SOURCE OF TRUTH for location display text.
 * All components should use this instead of building their own format.
 */
export function formatLocationText(geo: GeoLocation): string {
  if (!geo.countryCode) return '';
  
  // US: show "City, State" (e.g., "Austin, TX")
  if (geo.countryCode === 'US') {
    if (geo.city && geo.region) {
      return `${geo.city}, ${geo.region}`;
    }
    if (geo.city) return geo.city;
    if (geo.regionName) return geo.regionName;
    return 'United States';
  }
  
  // International: show "City, Country" or just "Country"
  if (geo.city && geo.country) {
    return `${geo.city}, ${geo.country}`;
  }
  return geo.country || '';
}
